import React, { useMemo } from 'react';
import DataGrid from '#/common/components/DataGrid';
import type { ColumnProps, PredefinedAction } from '#/common/components/DataGrid';
import { StyledNuvoBox } from '#/common/styles/template';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { InfoLabel, DataGridWrapper } from '../styles';

export interface PreviewStepBaseProps {
  infoText?: React.ReactNode;
  warningNode?: React.ReactNode;
  dataGridId: string;
  pluralText: string;
  singularText: string;
  columns: ColumnProps<Record<string, string>>[];
  dataSource: Record<string, unknown>[];
  noColumnChooser?: boolean;
  defaultPageSize?: number;
  disableSorting?: boolean;
  disableFiltering?: boolean;
}

/**
 * Shared layout for the import-preview step (Asset, Work Order, ...). Entity-specific
 * concerns (dataSource transform, columns config, warning copy) are computed by the caller.
 */
const PreviewStepBase: React.FC<PreviewStepBaseProps> = ({
  infoText,
  warningNode,
  dataGridId,
  pluralText,
  singularText,
  columns,
  dataSource,
  noColumnChooser,
  defaultPageSize,
  disableSorting = true,
  disableFiltering = true,
}) => {
  const { isDesktop } = useViewport();

  const actions = useMemo(() => {
    const allActions: PredefinedAction[] = [];

    if (!noColumnChooser) {
      allActions.push('column-chooser');
    }

    if (!disableFiltering) {
      allActions.push('clear-filters');
    }

    return allActions;
  }, [noColumnChooser, disableFiltering]);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="box-caption">
        <NuvoLabel>Preview</NuvoLabel>
      </div>
      {infoText && <InfoLabel>{infoText}</InfoLabel>}
      {warningNode}
      <DataGridWrapper>
        <DataGrid
          id={dataGridId}
          pluralText={pluralText}
          singularText={singularText}
          selectionMode="none"
          actions={actions}
          columns={columns}
          dataSource={dataSource}
          disablePaging={!defaultPageSize}
          defaultPageSize={defaultPageSize}
          disableSorting={disableSorting}
          disableFiltering={disableFiltering}
        />
      </DataGridWrapper>
    </StyledNuvoBox>
  );
};

export default React.memo(PreviewStepBase);
